import * as React from 'react';
import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * The capsule: one pill shape, three jobs.
 *
 * The app had ~177 hand-rolled `rounded-full px-*` controls that disagreed on
 * radius, padding, border width, shadow and type scale — tab rails, filter
 * pills, status badges and removable chips all reinventing the same shape.
 *
 * They are deliberately NOT collapsed into one control. A tab is pressable and
 * carries a count; a status badge is not interactive and carries meaning in its
 * colour; a chip is removable. Flattening those would make the UI look
 * consistent while lying about what each thing does. So: one shared shape and
 * type scale, three honest behaviours.
 *
 *   <Capsule variant="tab" icon={Wrench} count={7} selected>Plumbing</Capsule>
 *   <Capsule variant="status" tone="success">Approved</Capsule>
 *   <Capsule variant="chip" onRemove={…}>Deep Cleaning</Capsule>
 */

type Variant = 'tab' | 'status' | 'chip';
type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/**
 * Semantic tones only. Per the design system there is no per-category colour
 * cycling — colour here means state, never identity.
 */
const TONES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground border-border',
  success: 'bg-success/10 text-success border-success/25',
  warning: 'bg-brand-amber/15 text-foreground border-brand-amber/35',
  danger: 'bg-destructive/10 text-destructive border-destructive/25',
  info: 'bg-secondary text-secondary-foreground border-border',
};

export interface CapsuleProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: Variant;
  icon?: LucideIcon;
  /** Trailing number. Omitted entirely when undefined — never rendered as 0. */
  count?: number;
  /** `tab` only. Active tabs drop the shadow so they read as pressed. */
  selected?: boolean;
  /** `status` only. */
  tone?: Tone;
  /** `chip` only. Presence of this adds the remove affordance. */
  onRemove?: () => void;
  removeLabel?: string;
  children: React.ReactNode;
}

export const Capsule = React.forwardRef<HTMLButtonElement, CapsuleProps>(
  function Capsule(
    { variant = 'tab', icon: Icon, count, selected, tone = 'neutral',
      onRemove, removeLabel, className, children, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const base = cn(
      'inline-flex shrink-0 items-center gap-2 rounded-full border',
      'px-4 py-2 text-sm font-bold leading-none tracking-[-0.005em]',
      'transition-[transform,box-shadow,background-color,border-color] duration-150',
    );

    const body = (
      <>
        {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
        <span className="whitespace-nowrap">{children}</span>
        {count !== undefined && (
          <span
            className={cn(
              'tabular-nums font-bold',
              variant === 'tab' && !selected && 'text-muted-foreground',
            )}
          >
            {count}
          </span>
        )}
      </>
    );

    if (variant === 'status') {
      return (
        <span data-capsule="status" className={cn(base, 'cursor-default', TONES[tone], className)}>
          {body}
        </span>
      );
    }

    if (variant === 'chip') {
      // A removable chip is something the user chose, so it wears the palette's
      // chosen pairing (teal tint / teal text) — the same as a selected tab and
      // the GroupedSelect chips — rather than the card-white it had. gap-1.5
      // tightens the label-to-X spacing, and the remove circle hugs the icon
      // instead of floating it in a wide box.
      return (
        <span
          data-capsule="chip"
          className={cn(
            base,
            'gap-1.5 border-success/30 bg-secondary text-success',
            onRemove && 'pr-1.5',
            className,
          )}
        >
          {body}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeLabel ?? t('a11yRemoveNamedItem', {
                name: typeof children === 'string' ? children : '',
              })}
              className="grid h-5 w-5 place-items-center rounded-full text-success/70 transition-colors hover:bg-success/15 hover:text-success"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          )}
        </span>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        data-capsule="tab"
        aria-pressed={selected}
        className={cn(
          base,
          'min-h-[40px] cursor-pointer',
          selected
            // Filled tint, no shadow: pressed, not merely coloured.
            ? 'border-success/25 bg-secondary text-success'
            : 'border-border bg-card text-foreground shadow-e1 hover:-translate-y-px hover:shadow-e2',
          className,
        )}
        {...props}
      >
        {body}
      </button>
    );
  },
);

/**
 * Horizontal rail for a row of capsules. Scrolls rather than wraps, so a
 * growing catalogue never pushes the content below it down the page.
 */
export function CapsuleRail({
  className, children, ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex gap-2.5 overflow-x-auto pb-2 pt-1',
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
